import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addPartnerClientId, cancelPartnerClientId, deletePartnerClientId, fetchPartnerClientIds, updatePartnerClientId } from "../actions/partnerClientIdsActions";
import { error } from "console";

export interface PartnerClientId {
  id: string;
  clientId: string;
  businessId: string;
  created_at?: string;
  users_limit: number;
  users_count: number;
  business?: string;
  /** 'canceled' once the bucket has been retired ("reset to elisyan"). The
   * list endpoint still returns these; the clients screen renders them
   * dimmed with the edit/cancel actions disabled. */
  status?: "canceled" | string;
  [key: string]: any;
}

interface PartnerClientIdState {
  items: PartnerClientId[];
  loading: boolean;
  error: string | null;
  page: number;
  limit: number;
  total: number;
  lastDocId: string | null;
}

const initialState: PartnerClientIdState = {
  items: [],
  loading: false,
  error: null,
  page: 1,
  limit: 10,
  total: 0,
  lastDocId: null,
};


/** ------------------- SLICE ------------------- **/

const partnerClientIdSlice = createSlice({
  name: "partnerClientIds",
  initialState,
  reducers: {
    resetState: (state: PartnerClientIdState) => ({ ...state, error: null, loading: false }),
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPartnerClientIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPartnerClientIds.fulfilled, (state, action) => {
        console.log('fetched partnerClientIds', action.payload);
        state.loading = false;
      
        if (action.payload.search) {
          // Search: replace items entirely
          state.items = action.payload.data;
        } else {
          // Pagination: merge with existing items
          const itemsMap = new Map([
            ...state.items.map((item) => [item.id, item] as const),
            ...action.payload.data.map((item) => [item.id, item] as const),
          ]);
          state.items = Array.from(itemsMap.values());
        }
      
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
        state.lastDocId = action.payload.lastDocId;
      })
      .addCase(fetchPartnerClientIds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to fetch partnerClientIds";
      })

      // Add
      .addCase(addPartnerClientId.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      }).addCase(addPartnerClientId.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      }).addCase(addPartnerClientId.rejected, (state, action) => {
        state.loading = false;
        console.log('error adding clientId', action.error.message, action.error, action.payload)
        state.error = action.payload ?? "Failed to add ClientId";
      })

      // Update
      .addCase(updatePartnerClientId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePartnerClientId.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) state.items[index] = action.payload;
      }).addCase(updatePartnerClientId.rejected, (state, action) => {
        state.loading = false;
        console.log('error updating clientId', action.error.message, action.error, action.payload)
        state.error = action.payload ?? "Failed to update ClientId";
      })
      // Delete
      .addCase(deletePartnerClientId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePartnerClientId.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      }).addCase(deletePartnerClientId.rejected, (state, action) => {
        state.loading = false;
        console.log('error deleting clientId', action, action.payload)
        state.error = action.payload ?? "Failed to delete ClientId";
      })
      // Cancel ("reset to elisyan")
      .addCase(cancelPartnerClientId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelPartnerClientId.fulfilled, (state, action) => {
        state.loading = false;
        // A canceled client ID is kept in the list (shown dimmed, actions
        // disabled) rather than removed — mark it canceled and freeze its
        // capacity to match the backend (users_limit := users_count).
        state.items = state.items.map((item) =>
          item.id === action.payload.id
            ? { ...item, status: "canceled", users_limit: item.users_count }
            : item
        );
      })
      .addCase(cancelPartnerClientId.rejected, (state, action) => {
        state.loading = false;
        console.log('error canceling clientId', action, action.payload)
        state.error = action.payload ?? "Failed to cancel ClientId";
      });
  },
});

export default partnerClientIdSlice.reducer;
export const { resetState } = partnerClientIdSlice.actions;
